import React, { useEffect, useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { fetchNotifications, markNotificationRead, fetchUserByEmail, Notification } from "../utils/api";

const NotificationBell: React.FC = () => {
    const { user } = useUser();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // Anchor coords for the fixed-positioned panel. Sidebar has overflow-y-auto
    // (which clips both axes per CSS spec), so an absolutely positioned panel
    // gets clipped. Fixed positioning escapes the sidebar; we anchor it to the
    // button's viewport rect and open upward since the bell sits at the bottom.
    const [anchor, setAnchor] = useState<{ bottom: number; left: number } | null>(null);
    useEffect(() => {
        const loadNotifications = async () => {
            if (!user?.primaryEmailAddress?.emailAddress) return;
            try {
                const dbUser = await fetchUserByEmail(user.primaryEmailAddress.emailAddress);
                const notifs = await fetchNotifications(dbUser.id);
                setNotifications(notifs);
            } catch {
                // Silently fail
            }
        };

        loadNotifications();
        // Poll every 30 seconds
        const interval = setInterval(loadNotifications, 30000);
        return () => clearInterval(interval);
    }, [user?.primaryEmailAddress?.emailAddress]);

    const unreadCount = notifications.filter(n => !n.isRead).length;

    // Recompute anchor whenever the panel opens, and on resize/scroll while open.
    useEffect(() => {
        if (!open) return;
        const recalc = () => {
            const el = buttonRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            // viewport.height - r.top gives the distance from button-top to viewport
            // bottom — i.e. how far up from the bottom the panel's bottom edge
            // should sit (we open upward).
            setAnchor({ bottom: window.innerHeight - r.top + 8, left: r.left });
        };
        recalc();
        window.addEventListener("resize", recalc);
        window.addEventListener("scroll", recalc, true);
        return () => {
            window.removeEventListener("resize", recalc);
            window.removeEventListener("scroll", recalc, true);
        };
    }, [open]);

    // Close on click outside the button + panel.
    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (buttonRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const handleMarkRead = async (id: string) => {
        try {
            await markNotificationRead(id);
            setNotifications(notifications.map(n =>
                n.id === id ? { ...n, isRead: true } : n
            ));
        } catch {
            // Silently fail
        }
    };

    const typeColors: Record<string, string> = {
        success: 'bg-green-100 text-green-800',
        error: 'bg-red-100 text-red-800',
        info: 'bg-blue-100 text-blue-800',
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setOpen(!open)}
                className="relative p-2 rounded-lg text-coffee-200 hover:text-white hover:bg-coffee-700 transition-colors"
                aria-label="Notifications"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && anchor && (
                <div
                    ref={panelRef}
                    style={{ position: "fixed", bottom: anchor.bottom, left: anchor.left }}
                    className="w-80 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-2xl border border-coffee-200 overflow-hidden z-[60]"
                >
                    <div className="p-3 border-b border-coffee-200 flex justify-between items-center">
                        <h4 className="font-semibold text-coffee-900">Notifications</h4>
                        {unreadCount > 0 && (
                            <span className="text-xs text-coffee-500">{unreadCount} unread</span>
                        )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length > 0 ? notifications.slice(0, 10).map(n => (
                            <div
                                key={n.id}
                                className={`p-3 border-b border-coffee-100 cursor-pointer hover:bg-coffee-50 transition-colors ${!n.isRead ? 'bg-coffee-50' : ''}`}
                                onClick={() => !n.isRead && handleMarkRead(n.id)}
                            >
                                <div className="flex items-start gap-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[n.type] || typeColors.info}`}>
                                        {n.type}
                                    </span>
                                    {!n.isRead && <span className="w-2 h-2 bg-coffee-600 rounded-full flex-shrink-0 mt-1"></span>}
                                </div>
                                <p className="text-sm font-medium text-coffee-900 mt-1">{n.title}</p>
                                <p className="text-xs text-coffee-600 mt-0.5">{n.message}</p>
                                <p className="text-xs text-coffee-400 mt-1">{new Date(n.createdAt).toLocaleDateString()}</p>
                            </div>
                        )) : (
                            <div className="p-6 text-center text-coffee-500 text-sm">
                                No notifications yet
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
